import React, { useEffect, useMemo, useRef } from 'react'
import { Input } from '@skop-app/lib-front'
import { cx } from 'class-variance-authority'
import { useDebouncedCallback } from 'use-debounce'

import { useGridApi, useGridState } from './GridRoot'
import { GRID_TOOLBAR_INPUT_CLASSNAME } from './toolbarControlStyles'
import type { GridSearchProps } from '../types'

export function GridSearch({
  placeholder,
  debounceMs = 300,
  value,
  defaultValue,
  onValueChange,
  className,
  onFocus,
}: GridSearchProps) {
  const { api, gridIsReady } = useGridApi()
  const { quickSearchValue, setQuickSearchValue } = useGridState()

  const isControlled = typeof value === 'string'
  const resolvedValue = isControlled ? value : quickSearchValue

  const debouncedSearch = useDebouncedCallback((nextValue: string) => {
    if (!api || !gridIsReady) return
    api.setGridOption('quickFilterText', nextValue)
  }, debounceMs)

  useEffect(() => {
    if (!isControlled) return
    if (typeof value !== 'string') return
    setQuickSearchValue(value)
    debouncedSearch(value)
  }, [debouncedSearch, isControlled, setQuickSearchValue, value])

  // Apply default value once on mount if provided.
  useEffect(() => {
    if (isControlled) return
    if (typeof defaultValue !== 'string') return
    if (defaultValue === quickSearchValue) return
    setQuickSearchValue(defaultValue)
    debouncedSearch(defaultValue)
  }, [
    debouncedSearch,
    defaultValue,
    isControlled,
    quickSearchValue,
    setQuickSearchValue,
  ])

  const initialApplyRef = useRef(false)

  // Ensure the current search value is applied once the grid becomes ready.
  useEffect(() => {
    if (!gridIsReady) return
    if (initialApplyRef.current) return
    if (!api) return
    api.setGridOption('quickFilterText', resolvedValue)
    initialApplyRef.current = true
  }, [api, gridIsReady, resolvedValue])

  const inputHandlers = useMemo(() => {
    return {
      onInput: (event: React.FormEvent<HTMLInputElement>) => {
        const nextValue = event.currentTarget.value
        if (!isControlled) setQuickSearchValue(nextValue)
        onValueChange?.(nextValue)
        debouncedSearch(nextValue)
      },
    }
  }, [debouncedSearch, isControlled, onValueChange, setQuickSearchValue])

  return (
    <Input
      type="search"
      name="search"
      placeholder={placeholder}
      value={resolvedValue}
      {...inputHandlers}
      onFocus={onFocus}
      className={cx(GRID_TOOLBAR_INPUT_CLASSNAME, className)}
    />
  )
}
